import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Copy, Loader2, FileText } from "lucide-react";
import { API_ENDPOINTS } from "@/config/api";
import { reportUnauthorized } from "@/services/api";
import { Toaster, toast } from "sonner";

export default function ValidationAgentPrompt() {
  const [productId, setProductId] = useState("");
  const [variantId, setVariantId] = useState("");
  const [prompt, setPrompt] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleFetchPrompt = async () => {
    if (!productId.trim() && !variantId.trim()) {
      toast.error("Enter at least a product ID or variant ID");
      return;
    }
    setLoading(true);
    setPrompt(null);
    try {
      const p = new URLSearchParams();
      if (productId.trim()) p.set("product_id", productId.trim());
      if (variantId.trim()) p.set("variant_id", variantId.trim());
      const url = `${API_ENDPOINTS.validationAgentPrompt}?${p.toString()}`;
      const result = await fetch(url, { credentials: "include" });
      if (!result.ok) {
        reportUnauthorized(API_ENDPOINTS.validationAgentPrompt, result.status);
        throw new Error(`Request failed: ${result.status}`);
      }
      const contentType = result.headers.get("content-type") ?? "";
      let text: string;
      if (contentType.includes("application/json")) {
        const data = await result.json();
        text =
          typeof data === "string"
            ? data
            : (data?.prompt ?? data?.text ?? JSON.stringify(data));
      } else {
        text = await result.text();
      }
      setPrompt(text);
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to fetch prompt",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    if (!prompt) return;
    try {
      await navigator.clipboard.writeText(prompt);
      toast.success("Prompt copied to clipboard");
    } catch {
      toast.error("Failed to copy");
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">
          Agent Validation Prompt
        </h1>
        <p className="text-muted-foreground mt-1">
          Request an agent prompt for running validation on a specific product
          or variant. Copy the prompt and paste it into the agent.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="size-4" />
            Request Prompt
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            Enter a product ID and/or variant ID.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="product-id">Product ID</Label>
              <Input
                id="product-id"
                placeholder="e.g. 12345"
                value={productId}
                onChange={(e) => setProductId(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="variant-id">Variant ID</Label>
              <Input
                id="variant-id"
                placeholder="e.g. 67890"
                value={variantId}
                onChange={(e) => setVariantId(e.target.value)}
              />
            </div>
          </div>

          <Button onClick={handleFetchPrompt} disabled={loading}>
            {loading ? (
              <>
                <Loader2 className="mr-2 size-4 animate-spin" />
                Fetching...
              </>
            ) : (
              "Fetch Prompt"
            )}
          </Button>
        </CardContent>
      </Card>

      {prompt && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle>Prompt</CardTitle>
            <Button variant="outline" size="sm" onClick={handleCopy}>
              <Copy className="mr-2 size-4" />
              Copy
            </Button>
          </CardHeader>
          <CardContent>
            <Textarea
              readOnly
              className="min-h-[200px] font-mono text-sm"
              value={prompt}
            />
          </CardContent>
        </Card>
      )}

      <Toaster />
    </div>
  );
}
